/**
 * AGC: BattleLog Component - Reusable battle log with turn breakdown
 * AGC: Displays turn-by-turn battle history with damage breakdowns and buff information
 * AGC: Used in HybridPvEBattle, InteractiveP2PBattle, and other battle components
 * AGC: Each turn is displayed as a swipeable carousel page
 */

import React, { useState, useMemo } from 'react'
import { formatRarityLabel, getRarityBadgeClasses } from '../../utils/rarityBadges'
import { Choice, Element } from './BattleCenter'
import { SwipeCarousel } from '../common/SwipeCarousel'

export interface DamageBreakdown {
  baseName: string
  baseDamage: number
  levelBonus: number
  elementBonus: number
  elementName: string
  rarityBonus: number
  rarityName: string
  hasteBonus: number
  rawDamage: number
  defense: number
  afterDefense: number
  pierceReduction?: number
  shieldReduction?: number
  frostReduction?: number
  // AGC: Mana potion boost (NEW - 2025-11-28)
  manaBoost?: number             // Mana boost multiplier (e.g., 1.05 = +5% buff power)
  // AGC: Potion effect fields (NEW - 2025-11-25)
  potionHealing?: number         // Energy restored by healing potion
  potionPoisonReceived?: number  // Damage taken from opponent's poison potion
  potionShieldReduction?: number // Damage blocked by shield potion
  // AGC: Double Strike potion effect (NEW - 2025-11-27)
  doubleStrikeBonus?: number     // Bonus damage from Double Strike potion
  doubleStrikePercent?: number   // Percentage of Double Strike bonus (e.g. 25 for +25%)
}

export interface TurnResult {
  turnNumber: number
  yourDamage: number
  opponentDamage: number
  yourEnergy: number
  opponentEnergy: number
  yourChoice: string
  opponentChoice: string
  yourElement: string
  opponentElement: string
  yourBuffs?: number[]
  opponentBuffs?: number[]
  yourActivatedBuff?: string | null
  opponentActivatedBuff?: string | null
  yourFrostUsed?: boolean
  opponentFrostUsed?: boolean
  yourBreakdown?: DamageBreakdown
  opponentBreakdown?: DamageBreakdown
}

export interface BattleLogProps {
  battleLog: TurnResult[]
  getChoiceImage: (choice: string | Choice) => string
  getElementEmoji: (element: string | Element) => string
  opponentLabel?: string // "Bot" or "Opponent"
}

// AGC: Using PNG images instead of emojis
const BUFF_IMAGES: Record<number, string> = { 0: '/shield.png', 1: '/pierce.png', 2: '/haste.png', 3: '/frost.png' }
const BUFF_NAMES: Record<number, string> = { 0: 'SHIELD', 1: 'PIERCE', 2: 'HASTE', 3: 'FROST' }

// AGC: Determine element relationship based on Water > Fire > Ice > Wind > Water cycle
const determineElementRelationship = (attackerElement: string | null | undefined, defenderElement: string | null | undefined): 'WON' | 'LOST' | 'NEUTRAL' | 'DRAW' => {
  if (!attackerElement || !defenderElement) {
    return 'NEUTRAL'
  }
  
  const attacker = attackerElement.toUpperCase()
  const defender = defenderElement.toUpperCase()
  
  // AGC: Same element = DRAW
  if (attacker === defender) {
    return 'DRAW'
  }
  
  // AGC: Element advantage cycle: Water > Fire > Ice > Wind > Water
  const advantages: Record<string, string> = {
    WATER: 'FIRE',
    FIRE: 'ICE',
    ICE: 'WIND',
    WIND: 'WATER'
  }
  
  // AGC: Check if attacker beats defender
  if (advantages[attacker] === defender) {
    return 'WON'
  }
  
  // AGC: Check if defender beats attacker
  if (advantages[defender] === attacker) {
    return 'LOST'
  }
  
  // AGC: Non-adjacent elements (neither beats the other) = NEUTRAL
  return 'NEUTRAL'
}

// AGC: Build base summary in HERO format (same as BattleTurnModal)
const buildBaseSummary = (breakdown: DamageBreakdown, attackerElement?: string | null, defenderElement?: string | null) => {
  const base = Math.round(breakdown.baseDamage ?? 0)
  const level = Math.round(breakdown.levelBonus ?? 0)
  const element = Math.round(breakdown.elementBonus ?? 0)
  
  // AGC: Determine HERO status from baseName (WIN/LOSE/DRAW or choice name)
  const baseNameUpper = (breakdown.baseName ?? '').toUpperCase()
  let heroStatus = 'DRAW'
  if (baseNameUpper.includes('WIN')) {
    heroStatus = 'WIN'
  } else if (baseNameUpper.includes('LOSE') || baseNameUpper.includes('LOST')) {
    heroStatus = 'LOSE'
  } else if (baseNameUpper.includes('DRAW')) {
    heroStatus = 'DRAW'
  }
  
  // AGC: Determine ELEMENT status from element relationship
  const elementRelationship = determineElementRelationship(attackerElement, defenderElement)
  let elementStatus = elementRelationship
  let elementValue = '0'
  
  if (elementRelationship === 'WON') {
    elementValue = `+${Math.abs(element)}`
  } else if (elementRelationship === 'LOST') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'NEUTRAL') {
    elementValue = `-${Math.abs(element)}`
  } else if (elementRelationship === 'DRAW') {
    elementValue = '0'
  } else {
    // AGC: Fallback to actual element bonus value if relationship can't be determined
    elementValue = element !== 0 ? (element > 0 ? `+${element}` : `${element}`) : '0'
  }
  
  const levelPart = level !== 0 ? ` + Level ${level}` : ''
  const elementPart = ` + ELEMENT: (${elementStatus}) ${elementValue}`
  const total = base + level + element

  return {
    summary: `HERO: (${heroStatus}) ${base}${levelPart}${elementPart} = ${total}`,
    baseTotal: total,
  }
}

// AGC: Build buff summary (adapted for BattleLog format)
const buildBuffSummary = (
  breakdown: DamageBreakdown,
  baseTotal: number,
  buffs?: number[],
  activatedBuff?: string | null
) => {
  // AGC: FIX 2025-11-29 - Show buff as percentage multiplier when mana is active
  const pierce = Math.round(breakdown.pierceReduction ?? 0)
  const haste = Math.round(breakdown.hasteBonus ?? 0)
  const manaBoost = breakdown.manaBoost && breakdown.manaBoost > 1.0 ? breakdown.manaBoost : null
  const contributions: string[] = []
  let runningTotal = baseTotal

  if (pierce !== 0) {
    runningTotal += pierce
    // AGC: Calculate percentage for PIERCE (base 25%, boosted by mana)
    if (manaBoost && baseTotal > 0) {
      const basePiercePercent = 25
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      const totalPercent = basePiercePercent + manaPercent
      contributions.push(`PIERCE (${basePiercePercent}%+${manaPercent}% Mana = ${totalPercent}%) ${formatSigned(pierce)} ⚡`)
    } else if (baseTotal > 0) {
      contributions.push(`PIERCE (25%) ${formatSigned(pierce)} ⚡`)
    } else {
      contributions.push(`${formatSigned(pierce)} from PIERCE ⚡`)
    }
  }

  if (haste !== 0) {
    runningTotal += haste
    // AGC: Calculate percentage for HASTE (flat +5, boosted by mana)
    if (manaBoost) {
      const manaPercent = Math.round((manaBoost - 1.0) * 100)
      contributions.push(`HASTE (+${manaPercent}% Mana) ${formatSigned(haste)} ⚡`)
    } else {
      contributions.push(`HASTE ${formatSigned(haste)} ⚡`)
    }
  }

  // AGC: Get highlighted buffs (active/used buffs)
  const highlightedBuffs: string[] = []
  if (buffs && buffs.length > 0) {
    buffs.forEach((buffId) => {
      const buffName = BUFF_NAMES[buffId]
      // AGC: Include buff if it was activated, or if it contributed to damage (PIERCE/HASTE)
      if (activatedBuff === buffName || (buffName === 'PIERCE' && pierce !== 0) || (buffName === 'HASTE' && haste !== 0)) {
        highlightedBuffs.push(buffName.toUpperCase())
      }
    })
  }

  const numericSegment = contributions.length > 0 ? `${contributions.join(' + ')} = ${runningTotal}` : null
  const labelSegment = highlightedBuffs.length > 0 && contributions.length === 0 ? highlightedBuffs.join(', ') : null
  const summarySegments = [labelSegment, numericSegment].filter(Boolean)

  return {
    summary: summarySegments.length > 0 ? summarySegments.join(' | ') : 'none',
    total: runningTotal,
  }
}

// AGC: Build rarity summary (same as BattleTurnModal)
const buildRaritySummary = (breakdown: DamageBreakdown, currentTotal: number) => {
  const rarityAmount = Math.round(breakdown.rarityBonus ?? 0)
  const rarityName = (breakdown.rarityName ?? 'none').toUpperCase()

  if (rarityAmount === 0) {
    return {
      summary: rarityName === 'NONE' ? 'none' : rarityName,
      total: currentTotal,
    }
  }

  const total = currentTotal + rarityAmount
  return {
    summary: `${rarityName} ${formatSigned(rarityAmount)} = ${total}`,
    total,
  }
}

// AGC: Build special effect lines (same as BattleTurnModal)
const buildSpecialEffectLines = (type: 'you' | 'opponent', breakdown: DamageBreakdown, opponentLabel: string, otherBreakdown?: DamageBreakdown) => {
  const messages: Array<{ text: string; tone: 'danger' | 'info' | 'success' }> = []

  // AGC: Mana potion boost (NEW - 2025-11-28) - Shows rarity-scaled percentage
  // AGC: FIX - Mana message should appear in YOUR breakdown EVERY TURN it's active
  // AGC: The manaBoost is stored in opponent's breakdown (because it affects their damage to you)
  // AGC: So we check otherBreakdown (opponent's) when rendering YOUR side
  if (type === 'you' && otherBreakdown?.manaBoost && otherBreakdown.manaBoost > 1.0) {
    const manaPercent = ((otherBreakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
    // AGC: Check if YOUR buff was activated this turn (shield/pierce effects are in otherBreakdown)
    const buffWasActivated = otherBreakdown?.shieldReduction || breakdown?.pierceReduction
    const message = buffWasActivated
      ? `💧 Mana Potion boosting your buff! +${manaPercent}%`
      : `💧 Mana Potion active! +${manaPercent}% on next buff`
    messages.push({
      text: message,
      tone: 'success',
    })
  }

  // AGC: Buff effects - Shield message shows mana bonus inline
  if (type === 'opponent' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
      : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({
      text: `Blocked by your SHIELD! -${Math.round(breakdown.shieldReduction)} damage${manaNote}`,
      tone: 'info',
    })
  }

  if (type === 'you' && breakdown.shieldReduction && breakdown.shieldReduction > 0) {
    const manaPercent = breakdown.manaBoost && breakdown.manaBoost > 1.0 
      ? ((breakdown.manaBoost - 1.0) * 100).toFixed(1).replace(/\.0$/, '')
      : null
    const manaNote = manaPercent ? ` (+${manaPercent}% Mana)` : ''
    messages.push({
      text: `Blocked by ${opponentLabel.toLowerCase()}'s shield -${Math.round(breakdown.shieldReduction)} damage${manaNote}`,
      tone: 'danger',
    })
  }

  if (type === 'you' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({
      text: `Frozen by ${opponentLabel.toLowerCase()}! -${Math.round(breakdown.frostReduction)} damage ❄️`,
      tone: 'danger',
    })
  }

  if (type === 'opponent' && breakdown.frostReduction && breakdown.frostReduction > 0) {
    messages.push({
      text: `Frozen by you! -${Math.round(breakdown.frostReduction)} damage ❄️`,
      tone: 'info',
    })
  }

  // AGC: Potion effects (NEW - 2025-11-25)
  if (breakdown.potionHealing && breakdown.potionHealing > 0) {
    messages.push({
      text: `🧪 Healing Potion! +${Math.round(breakdown.potionHealing)} energy`,
      tone: 'success',
    })
  }

  if (breakdown.potionPoisonReceived && breakdown.potionPoisonReceived > 0) {
    // AGC: Show poison damage with 1 decimal place (e.g., 2.7 instead of rounding to 3)
    const poisonDmg = breakdown.potionPoisonReceived % 1 === 0 
      ? Math.round(breakdown.potionPoisonReceived) 
      : breakdown.potionPoisonReceived.toFixed(1)
    messages.push({
      text: `☠️ Poisoned! -${poisonDmg} energy`,
      tone: 'danger',
    })
  }

  if (breakdown.potionShieldReduction && breakdown.potionShieldReduction > 0) {
    messages.push({
      text: `🧪 Shield Potion! -${Math.round(breakdown.potionShieldReduction)} damage blocked`,
      tone: 'info',
    })
  }

  // AGC: Double Strike potion effect (NEW - 2025-11-27)
  if (breakdown.doubleStrikeBonus && breakdown.doubleStrikeBonus > 0) {
    messages.push({
      text: `⚔️ Double Strike! +${Math.round(breakdown.doubleStrikeBonus)} damage (+${breakdown.doubleStrikePercent || 25}%)`,
      tone: 'success',
    })
  }

  return messages
}

// AGC: Format signed number (same as BattleTurnModal)
const formatSigned = (value: number) => {
  const rounded = Math.abs(Math.round(value))
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${rounded}`
}

const renderRarityPill = (rarityName?: string | null) => {
  const classes = getRarityBadgeClasses(rarityName)
  if (!classes) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      <span className={`px-2 py-1 rounded text-xs font-semibold ${classes}`}>
        {formatRarityLabel(rarityName)}
      </span>
    </div>
  )
}

/**
 * AGC: BattleLog - Reusable battle log component with carousel navigation
 * AGC: Shows turn-by-turn history with damage breakdowns
 * AGC: Each turn is a swipeable page in the carousel
 */
export const BattleLog: React.FC<BattleLogProps> = ({
  battleLog,
  getChoiceImage,
  getElementEmoji,
  opponentLabel = 'Opponent'
}) => {
  // AGC: Reverse battle log so newest turns appear first, then convert to carousel items
  const reversedLog = useMemo(() => [...battleLog].reverse(), [battleLog])
  const [selectedTurnIndex, setSelectedTurnIndex] = useState(0)

  if (battleLog.length === 0) {
    return null
  }

  return (
    <div className="border-t border-white/10 overflow-hidden w-full h-[500px]">
      <div className="py-2 border-b border-white/10 flex items-center justify-between px-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-white">Battle Log</h3>
          <span className="text-[10px] text-slate-400">
            Turn {selectedTurnIndex + 1} of {reversedLog.length}
          </span>
        </div>
      </div>

      <div className="h-[calc(500px-3rem)] overflow-hidden">
        <SwipeCarousel
          items={reversedLog}
          selectedIndex={selectedTurnIndex}
          onSelect={setSelectedTurnIndex}
          size="fullWidth"
          loop={false}
          renderItem={({ item: result, isActive }) => (
            <div className={`w-full h-full p-3 overflow-y-auto ${isActive ? 'opacity-100' : 'opacity-0'}`}>

            <div className="grid grid-cols-2 gap-2 mb-1.5">
              {/* AGC: Your Turn Result - data-tour="simulation-your-card" for step 27 */}
              <div data-tour="simulation-your-card" className="border border-white/10 rounded px-2 py-1.5">
                <div className="text-green-400 font-semibold text-[10px] mb-1">YOU</div>
                <div className="flex items-center space-x-1.5 mb-1">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <img
                      src={getChoiceImage(result.yourChoice)}
                      alt={result.yourChoice || 'unknown'}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        console.log('Player choice image error:', result.yourChoice, getChoiceImage(result.yourChoice))
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                  <img 
                    src={(() => {
                      const normalizedElem = result.yourElement?.toLowerCase()
                      switch (normalizedElem) {
                        case 'water': return '/water.png'
                        case 'fire': return '/fire.png'
                        case 'ice': return '/ice.png'
                        case 'wind': return '/wind.png'
                        default: return '/water.png'
                      }
                    })()} 
                    alt={result.yourElement || 'element'} 
                    className="w-4 h-4 object-contain"
                  />
                  <span className="text-white text-[10px]">{result.yourDamage} dmg</span>
                </div>

                {/* AGC: BUFF DISPLAY WITH COLOR CODING */}
                {result.yourBuffs && result.yourBuffs.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {result.yourBuffs.map((buffId, idx) => {
                      const buffName = BUFF_NAMES[buffId]
                      const isActivated = result.yourActivatedBuff === buffName
                      const isFrostUsed = buffName === 'FROST' && result.yourFrostUsed

                      let colorClass = 'bg-red-600 text-white'
                      if (isActivated) {
                        colorClass = 'bg-green-600 text-white font-bold'
                      } else if (isFrostUsed) {
                        colorClass = 'bg-slate-600 text-slate-300'
                      }

                      return (
                        <span key={idx} className={`px-1.5 py-0.5 rounded text-[9px] ${colorClass} flex items-center gap-1`}>
                          <img src={BUFF_IMAGES[buffId]} alt={buffName} className="w-3 h-3 object-contain" />
                          {buffName}
                        </span>
                      )
                    })}
                  </div>
                )}
                {renderRarityPill(result.yourBreakdown?.rarityName)}
              </div>

              {/* AGC: Opponent Turn Result */}
              <div data-tour="simulation-opponent-card" className="border border-white/10 rounded px-2 py-1.5">
                <div className="text-red-400 font-semibold text-[10px] mb-1">{opponentLabel.toUpperCase()}</div>
                <div className="flex items-center space-x-1.5 mb-1">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <img
                      src={getChoiceImage(result.opponentChoice)}
                      alt={result.opponentChoice || 'unknown'}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        console.log('Opponent choice image error:', result.opponentChoice, getChoiceImage(result.opponentChoice))
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                  <img 
                    src={(() => {
                      const normalizedElem = result.opponentElement?.toLowerCase()
                      switch (normalizedElem) {
                        case 'water': return '/water.png'
                        case 'fire': return '/fire.png'
                        case 'ice': return '/ice.png'
                        case 'wind': return '/wind.png'
                        default: return '/water.png'
                      }
                    })()} 
                    alt={result.opponentElement || 'element'} 
                    className="w-4 h-4 object-contain"
                  />
                  <span className="text-white text-[10px]">{result.opponentDamage} dmg</span>
                </div>

                {/* AGC: BUFF DISPLAY WITH COLOR CODING */}
                {result.opponentBuffs && result.opponentBuffs.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {result.opponentBuffs.map((buffId, idx) => {
                      const buffName = BUFF_NAMES[buffId]
                      const isActivated = result.opponentActivatedBuff === buffName
                      const isFrostUsed = buffName === 'FROST' && result.opponentFrostUsed

                      let colorClass = 'bg-red-600 text-white'
                      if (isActivated) {
                        colorClass = 'bg-green-600 text-white font-bold'
                      } else if (isFrostUsed) {
                        colorClass = 'bg-slate-600 text-slate-300'
                      }

                      return (
                        <span key={idx} className={`px-1.5 py-0.5 rounded text-[9px] ${colorClass} flex items-center gap-1`}>
                          <img src={BUFF_IMAGES[buffId]} alt={buffName} className="w-3 h-3 object-contain" />
                          {buffName}
                        </span>
                      )
                    })}
                  </div>
                )}
                {renderRarityPill(result.opponentBreakdown?.rarityName)}
              </div>
            </div>

            <div className="text-slate-300 text-[10px] mt-1.5 mb-3">
              Energy After: You {result.yourEnergy} vs {opponentLabel} {result.opponentEnergy}
            </div>

            {/* AGC: DAMAGE BREAKDOWN - Side by Side Grid (Full breakdown format from BattleTurnModal) */}
            <div className="grid grid-cols-2 gap-3">
              {/* AGC: Your Damage Breakdown */}
              {result.yourBreakdown && (() => {
                // AGC: FIX 2025-11-29 - Correct order: HERO → RARITY → BUFF (matches game engine)
                const { summary: baseLine, baseTotal } = buildBaseSummary(result.yourBreakdown, result.yourElement, result.opponentElement)
                const { summary: raritySummary, total: rarityTotal } = buildRaritySummary(result.yourBreakdown, baseTotal)
                const { summary: buffSummary, total: buffTotal } = buildBuffSummary(result.yourBreakdown, rarityTotal, result.yourBuffs, result.yourActivatedBuff)
                const defenseLabel = 'Opp Def'
                const defenseMultiplier = result.yourBreakdown.defense ? result.yourBreakdown.defense.toFixed(2) : '1.00'
                const defenseValue = Math.round(result.yourBreakdown.afterDefense ?? buffTotal * (result.yourBreakdown.defense ?? 1))
                const finalDamageValue = Math.round(result.yourDamage)
                // AGC: Pass opponentBreakdown so we can check for YOUR mana in correct side
                const specialMessages = buildSpecialEffectLines('you', result.yourBreakdown, opponentLabel, result.opponentBreakdown)

                return (
                  <div data-tour="simulation-breakdown-area" className="border border-white/10 rounded px-3 py-2.5">
                    <div className="text-green-400 font-semibold text-xs mb-1">Your Damage Breakdown:</div>
                    <div className="bg-slate-800 rounded-lg p-2 space-y-0.5 text-[10px]">
                      <p className="text-slate-100 leading-tight">{baseLine}</p>
                      <p className="text-slate-200 leading-tight">Rarity: {raritySummary}</p>
                      <p className="text-slate-200 leading-tight">Buff: {buffSummary}</p>
                      <p className="text-slate-300 leading-tight">
                        {defenseLabel} (×{defenseMultiplier}) = {defenseValue}
                      </p>
                      {specialMessages.map((message, idx) => (
                        <p key={`${message.text}-${idx}`} className={`leading-tight ${
                          message.tone === 'danger' ? 'text-red-300' : 
                          message.tone === 'success' ? 'text-green-300' : 
                          'text-blue-300'
                        }`}>
                          {message.text}
                        </p>
                      ))}
                      <p className="font-bold leading-tight text-green-300">Final: {finalDamageValue} dmg</p>
                    </div>
                  </div>
                )
              })()}

              {/* AGC: Opponent Damage Breakdown */}
              {result.opponentBreakdown && (() => {
                // AGC: FIX 2025-11-29 - Correct order: HERO → RARITY → BUFF (matches game engine)
                const { summary: baseLine, baseTotal } = buildBaseSummary(result.opponentBreakdown, result.opponentElement, result.yourElement)
                const { summary: raritySummary, total: rarityTotal } = buildRaritySummary(result.opponentBreakdown, baseTotal)
                const { summary: buffSummary, total: buffTotal } = buildBuffSummary(result.opponentBreakdown, rarityTotal, result.opponentBuffs, result.opponentActivatedBuff)
                const defenseLabel = 'Your Def'
                const defenseMultiplier = result.opponentBreakdown.defense ? result.opponentBreakdown.defense.toFixed(2) : '1.00'
                const defenseValue = Math.round(result.opponentBreakdown.afterDefense ?? buffTotal * (result.opponentBreakdown.defense ?? 1))
                const finalDamageValue = Math.round(result.opponentDamage)
                // AGC: Pass yourBreakdown so we can check for opponent's mana (if applicable)
                const specialMessages = buildSpecialEffectLines('opponent', result.opponentBreakdown, opponentLabel, result.yourBreakdown)

                return (
                  <div data-tour="simulation-opponent-breakdown" className="border border-white/10 rounded px-3 py-2.5">
                    <div className="text-red-400 font-semibold text-xs mb-1">{opponentLabel} Damage Breakdown:</div>
                    <div className="bg-slate-800 rounded-lg p-2 space-y-0.5 text-[10px]">
                      <p className="text-slate-100 leading-tight">{baseLine}</p>
                      <p className="text-slate-200 leading-tight">Rarity: {raritySummary}</p>
                      <p className="text-slate-200 leading-tight">Buff: {buffSummary}</p>
                      <p className="text-slate-300 leading-tight">
                        {defenseLabel} (×{defenseMultiplier}) = {defenseValue}
                      </p>
                      {specialMessages.map((message, idx) => (
                        <p key={`${message.text}-${idx}`} className={`leading-tight ${
                          message.tone === 'danger' ? 'text-red-300' : 
                          message.tone === 'success' ? 'text-green-300' : 
                          'text-blue-300'
                        }`}>
                          {message.text}
                        </p>
                      ))}
                      <p className="font-bold leading-tight text-red-300">Final: {finalDamageValue} dmg</p>
                    </div>
                  </div>
                )
              })()}
            </div>
            </div>
          )}
        />
      </div>
    </div>
  )
}

